import { basicContext } from "../../../../utils/contextUtils";
import Errors from "../../../../utils/Errors"
import _ from "lodash";
import moment from "moment";
import {setFieldEdition, setFormButtons, setFieldVisibility} from "../../../../../apps/KpModule/actions";
import { GET_OBJECT_SUCCESS } from "../../../../../apps/KpModule/actions/api";
import { generateMembershipDocuments, sendMembershipEmail, sendInformEmail, sendCancelEmail } from "../membershipFeeUtils";
import {getFormValues} from "../../../../../apps/KpModule/selectors";
import {decrypt} from "../../../../utils/crypto";

// --- Helper Functions ---

/**
 * Returns the expected membership fee based on the organization size ID.
 * @param {string} orgSizeId - The ID representing the organization size (e.g., '1' for P, '2' for M, '3' for G).
 * @returns {number|null} The expected fee amount, or null if the size ID is not recognized.
 */
function getExpectedAmount(orgSizeId) {
    const expectedAmounts = { '1': 50, '2': 100, '3': 150 }; // P:50, M:100, G:150
    return expectedAmounts[orgSizeId] || null;
}

/**
 * Calculates the fee amount and donation amount based on the received amount and expected amount.
 * @param {number} receivedAmount - The amount received from the organization.
 * @param {number} expectedAmount - The expected fee amount for the organization.
 * @returns {{feeAmount: number|null, donationAmount: number|null}} Object containing calculated fee and donation amounts.
 */
function calculateAmounts(receivedAmount, expectedAmount) {
    let feeAmount = null;
    let donationAmount = null;

    if (_.isNumber(receivedAmount) && _.isNumber(expectedAmount)) {
        if (receivedAmount >= expectedAmount) {
            feeAmount = expectedAmount;
            if (receivedAmount > expectedAmount) {
                donationAmount = receivedAmount - expectedAmount;
            } else {
                donationAmount = 0;
            }
        } else {
            feeAmount = null;
            donationAmount = null;
        }
    }
    return { feeAmount, donationAmount };
}

/**
 * Updates the form buttons visibility and state based on current form values.
 * @param {object} store - The Redux store object.
 * @param {object} formData - Form data
 */
function updateFormButtons(store, formData) {

    const exerciseStatus = _.get(formData, 'exercise.exerciseStatus.id');
    const currentContributionStatus = _.get(formData, 'status.id');
    const receivedAmount = _.get(formData, 'amount');
    const expectedAmount = getExpectedAmount(_.get(formData, 'member.id'));

    const isClosed = exerciseStatus === 'finalClosure';

    let buttons = [returnButton];

    if (!isClosed) {
        if (currentContributionStatus === 'settled') {
            buttons.unshift(cancelPaymentButton);
            return store.dispatch(setFormButtons(buttons))
        }

        if(!_.isNumber(receivedAmount)) return store.dispatch(setFormButtons(buttons));

        if (receivedAmount < expectedAmount) {
            buttons.unshift(validateAndSendButton, saveAndInformButton);
        } else if (['unsettled', 'pending'].includes(currentContributionStatus)) {
            buttons.unshift(validateAndSendButton);
        }
    }
    store.dispatch(setFormButtons(buttons));
}

// --- Module Buttons ---

const validateButton = {
    type: 'action',
    action: 'validate',
    tKey: 'Valider',
    bsStyle: 'primary',
    pristine: true
};

const validateAndSendButton = {
    type: 'action',
    action: 'validateAndSend',
    tKey: 'Valider',
    bsStyle: 'primary',
};

const saveAndInformButton = {
    type: 'action',
    action: 'saveAndInform',
    tKey: "Vérifier",
    bsStyle: 'warning',
    getUserConfirmation: true,
    confirmationMessage: "Il y a une incohérence entre le montant saisi et la taille de l’association. Envoyer demande de renseignement à l’association ?",
};

const cancelPaymentButton = {
    type: 'action',
    action: 'cancelPayment',
    tKey: 'Annuler affectation',
    bsStyle: 'danger',
};

const returnButton = {
    type: 'return',
    bsStyle: 'default',
    tKey: 'Quitter'
}

// --- Data Access Functions ---

async function findContributions(context, callback) {
    this.prepareContext(context, 'L', async (error, context) => {
        if (error) return callback(error);
        try {
            const exerciseFilter = _.get(context, 'data.exercise');
            let exercises;
            if (exerciseFilter && exerciseFilter.id) {
                exercises = [exerciseFilter];
            } else {
                exercises = await global.app.C.Exercise.find({...basicContext(context), fieldPath: ['id', 'code', 'exerciseStatus.id']});
            }

            if (!exercises || exercises.length === 0) {
                return callback(null, []);
            }

            const orgFilter = _.get(context, 'data.association');
            const orgQuery = {};
            if (orgFilter && orgFilter.length) {
                orgQuery._id = {$in: orgFilter.map(o => new global.ObjectID(o.id))};
            }

            const organizations = await global.app.C.Organization.find({
                ...basicContext(context),
                query: orgQuery,
                filters: ['isMemberAssociation'],
                fieldPath: [
                    'id', 'code', 'name', 'member.id', 'member.name',
                    'organizationMemberships.id', 'organizationMemberships.exercise.id', 'organizationMemberships.exercise.code',
                    'organizationMemberships.status.id', 'organizationMemberships.status.name', // New status field
                    'organizationMemberships.amount', 'organizationMemberships.feeAmount', 'organizationMemberships.donationAmount',
                    'organizationMemberships.paymentDate', 'organizationMemberships.paymentMethod.id', 'organizationMemberships.paymentMethod.name',
                    'organizationMemberships.sendingCounter', 'organizationMemberships.lastModificationDate', 'organizationMemberships.lastModificationUser.id', 'organizationMemberships.lastModificationUser.name',
                    'organizationMemberships.membershipCard.id', 'organizationMemberships.membershipCard.filename',
                    'organizationMemberships.accountStatement.id', 'organizationMemberships.accountStatement.filename',
                    'users.id', 'users.name', 'users.organizationReferent', 'users.active'
                ]
            });

            let allContributions = [];
            exercises.forEach(exercise => {
                const contributionsForExercise = organizations.map(org => {
                    const membership = (org.organizationMemberships || []).find(m => m.exercise.id === exercise.id);
                    const referents = (org.users || []).filter(u => u.organizationReferent && u.active);

                    const receivedAmount = membership ? membership.amount : null;
                    const feeAmount = membership ? membership.feeAmount : null;
                    const donationAmount = membership ? membership.donationAmount : null;

                    // Determine status: if membership.status exists, use it. Otherwise, derive from upToDate (for backward compatibility)
                    const status = membership ? membership.status : {id: 'unsettled', name: 'Non réglé'}

                    return {
                        id: `${org.id}_${exercise.id}`,
                        exercise: exercise,
                        organization: _.pick(org, ['id', 'code', 'name']),
                        member: org.member,
                        status: status,
                        amount: receivedAmount, // Montant reçu
                        feeAmount: feeAmount, // Montant cotisation
                        donationAmount: donationAmount, // Montant don
                        paymentDate: membership ? membership.paymentDate : null,
                        paymentMethod: membership ? membership.paymentMethod : null,
                        sendingCounter: membership ? membership.sendingCounter : 0,
                        organizationReferents: referents,
                        organizationReferentsNumber: referents.length || 0,
                        lastModificationDate: membership ? membership.lastModificationDate : null,
                        lastModificationUser: membership ? membership.lastModificationUser : null,
                        membershipCard: membership ? membership.membershipCard : null,
                        accountStatement: membership ? membership.accountStatement : null,
                    };
                });
                allContributions.push(...contributionsForExercise);
            });

            const contributionStatusFilter = _.get(context, 'data.cotisation');
            if (contributionStatusFilter && contributionStatusFilter.length > 0) {
                allContributions = allContributions.filter(c => {
                    return contributionStatusFilter.some(s => s.id === c.status?.id);
                });
            }

            callback(null, allContributions);

        } catch (error) {
            callback(error);
        }
    });
}

async function getContribution(id, context, callback) {
    this.prepareContext(context, 'R', async (error, context) => {
        if (error) return callback(error);
        try {
            const [orgId, exerciseId] = id.split('_');
            let membership;
            let previousMembership;
            let organization;
            let exercise;

            organization = await global.app.C.Organization.get(orgId, {
                ...basicContext(context),
                fieldPath: [
                    'id', 'code', 'name', 'member.id', 'member.name',
                    'users.id', 'users.firstname', 'users.lastname',
                    'users.mail', 'users.organizationReferent',
                    'users.phone', 'users.active', 'users.functions',
                    //'users.shelters',
                    'organizationMemberships.id', 'organizationMemberships.exercise.id', 'organizationMemberships.exercise.code',
                    'organizationMemberships.status.id', 'organizationMemberships.status.name', // New status field
                    'organizationMemberships.amount', 'organizationMemberships.feeAmount', 'organizationMemberships.donationAmount',
                    'organizationMemberships.paymentDate', 'organizationMemberships.paymentMethod.id', 'organizationMemberships.paymentMethod.name',
                    'organizationMemberships.sendingCounter', 'organizationMemberships.lastModificationDate', 'organizationMemberships.lastModificationUser.id', 'organizationMemberships.lastModificationUser.name',
                    'organizationMemberships.membershipCard.id', 'organizationMemberships.membershipCard.filename',
                    'organizationMemberships.accountStatement.id', 'organizationMemberships.accountStatement.filename'
                ]
            });
            exercise = await global.app.C.Exercise.get(exerciseId, {...basicContext(context), fieldPath: ['id', 'code', 'exerciseStatus.id']});
            const previousExercise = await global.app.C.Exercise.collection
                .find({
                    _id: { $ne: new global.ObjectID(exerciseId) },
                    startDate: { $lt: exercise.startDate },
                    // (optionnel) si tu veux rester dans la même "année/code"
                    // code: current.code
                })
                .sort({ startDate: -1})
                .limit(1)
                .next();

            if (organization) {
                membership = (organization.organizationMemberships || []).find(m => m.exercise.id === exerciseId);

                if(previousExercise) {
                    previousMembership = (organization.organizationMemberships || []).find(m => m.exercise.id === previousExercise._id.toString());
                }
            }

            const referents = (organization.users || []).filter(u => u.organizationReferent && u.active);

            const receivedAmount = membership ? membership.amount : null;
            const feeAmount = membership ? membership.feeAmount : null;
            const donationAmount = membership ? membership.donationAmount : null;

            const status = membership ? membership.status : {id: 'unsettled', name: 'Non réglé'};

            const tc = context.tc || function(t) {return t}

            const contribution = {
                id,
                exercise: exercise,
                organization: _.pick(organization, ['id', 'code', 'name']),
                member: organization.member,
                status: status,
                amount: receivedAmount,
                feeAmount: feeAmount,
                donationAmount: donationAmount,
                previousExerciseFee: previousMembership ? previousMembership.feeAmount: null,
                paymentDate: membership ? membership.paymentDate : null,
                paymentMethod: membership ? membership.paymentMethod : null,
                sendingCounter: membership?.sendingCounter || 0,
                organizationReferents: referents.map(object => (
                    {
                        ...object,
                        civility: tc(object.civility?.name),
                        fullName: `${object.firstname} ${object.lastname}`,
                        mail: decrypt(object.mail),
                        phone: decrypt(object.phone),
                        functions: object.functions.map(userFunction => userFunction.name).join(', '),
                        active: object.active,
                        //shelters: object.shelters.map(shelter => shelter.name).join(', '),
                    }
                )),
            };
            callback(null, contribution);
        } catch (e) {
            callback(e);
        }
    });
}

async function saveContribution(object, context, callback) {
    this.prepareContext(context, 'S', async (error, context) => {
        if (error) return callback(error);

        try {
            const orgId = object.organization.id;
            const organization = await global.app.C.Organization.get(orgId, {
                ...basicContext(context),
                fieldPath: [
                    'id', 'code', 'name', 'address', 'additionalAddress', 'zipCode', 'city',
                    'member.id', 'member.name',
                    'files',
                    'organizationMemberships.id',
                    'organizationMemberships.exercise.id',
                    'organizationMemberships.exercise.code',
                    'organizationMemberships.status.id',
                    'organizationMemberships.amount', 'organizationMemberships.feeAmount', 'organizationMemberships.donationAmount',
                    'organizationMemberships.paymentDate', 'organizationMemberships.paymentMethod.id',
                    'organizationMemberships.lastModificationDate', 'organizationMemberships.lastModificationUser.id',
                    'organizationMemberships.membershipCard.id', 'organizationMemberships.membershipCard.filename',
                    'organizationMemberships.accountStatement.id', 'organizationMemberships.accountStatement.filename',
                    'organizationMemberships.sendingCounter'
                ]
            });

            const membershipIndex = (organization.organizationMemberships || []).findIndex(m => m.exercise.id === object.exercise.id);
            let currentMembership = membershipIndex > -1 ? organization.organizationMemberships[membershipIndex] : null;

            const referents = _.get(object, 'organizationReferents', []);
            const expectedAmount = getExpectedAmount(_.get(object, 'member.id'));
            const receivedAmount = object.amount;
            const feeAmount = object.feeAmount;
            const donationAmount = object.donationAmount;

            let dataToSave = {
                paymentDate: object.paymentDate,
                amount: receivedAmount,
                feeAmount,
                donationAmount,
                paymentMethod: object.paymentMethod,
                lastModificationUser: { id: context.user.id, name: context.user.name },
                lastModificationDate: new Date(),
            };

            const addMetaDataToFile = file => ({
                ...file,
                date: moment().format("YYYY-MM-DD HH:mm"),
                user: _.pick(context.user, ["id", "name"]),
                group: _.pick(context.group, ["id", "name"])
            });

            switch (context.action) {
                case 'saveAndInform':
                    if (!_.isNumber(receivedAmount) || receivedAmount <= 0) {
                        return callback(new Errors.ValidationError("Le montant reçu doit être strictement positif."));
                    }
                    if (receivedAmount >= expectedAmount) {
                        return callback(new Errors.ValidationError("L'action 'Enregistrer et informer référent(s)' est uniquement disponible si le montant reçu est inférieur au montant attendu."));
                    }
                    /*
                    if (!referents?.length) {
                        return callback(new Errors.ValidationError("Action non autorisée car aucun référent n'est renseigné."));
                    }
                     */

                    dataToSave.status = {id: 'pending', name: 'À vérifier'};
                    dataToSave.membershipCard = null;
                    dataToSave.accountStatement = null;

                    await sendInformEmail(object, organization); // Use utility function
                    break;

                case 'cancelPayment':
                    if (object.status?.id !== 'settled') {
                        return callback(new Errors.ValidationError("L'action 'Annuler affectation' est uniquement disponible pour un règlement 'Réglé'."));
                    }

                    dataToSave.status = {id: 'unsettled', name: 'Non réglé'};
                    dataToSave.amount = null;
                    dataToSave.feeAmount = null;
                    dataToSave.donationAmount = null;
                    dataToSave.paymentDate = null;
                    dataToSave.paymentMethod = null;
                    dataToSave.membershipCard = null;
                    dataToSave.accountStatement = null;

                    // Remove generated files from organization files
                    organization.files = (organization.files || []).filter(f =>
                        f.id !== currentMembership?.membershipCard?.id && f.id !== currentMembership?.accountStatement?.id
                    );

                    await sendCancelEmail(object, organization); // Use utility function
                    break;

                case 'validate':
                case 'validateAndSend':
                    if (!_.isNumber(receivedAmount) || receivedAmount <= 0) {
                        return callback(new Errors.ValidationError("Le montant reçu doit être strictement positif"));
                    }
                    if (!object.paymentDate) {
                        return callback(new Errors.ValidationError("La date de règlement est obligatoire"));
                    }
                    if (!object.paymentMethod) {
                        return callback(new Errors.ValidationError("Le mode de règlement est obligatoire"));
                    }
                    /*
                    if (context.action === 'validateAndSend' && !referents.length ) {
                        return callback(new Errors.ValidationError("Action non autorisée car aucun référent n'est renseigné"));
                    }
                    */
                    if (_.isNumber(receivedAmount) && _.isNumber(expectedAmount) && receivedAmount < expectedAmount) {
                        return callback(new Errors.ValidationError("Le règlement saisi est incompatible avec la taille de l’association"));
                    }

                    dataToSave.status = {id: 'settled', name: 'Réglé'};

                    const { cardFile, statementFile } = await generateMembershipDocuments(object, organization);
                    dataToSave.membershipCard = cardFile;
                    dataToSave.accountStatement = statementFile;

                    if (context.action === 'validateAndSend') {
                        dataToSave.sendingCounter = (object.sendingCounter || 0) + 1;
                        await sendMembershipEmail(object, organization, { cardFile, statementFile }, context);
                    }

                    // Add generated files to the main organization files, removing old ones if they exist
                    organization.files = (organization.files || []).filter(f =>
                        f.id !== currentMembership?.membershipCard?.id && f.id !== currentMembership?.accountStatement?.id
                    );
                    organization.files.push(addMetaDataToFile(cardFile), addMetaDataToFile(statementFile));
                    break;

                default:
                    // For other actions, just save the object as is (e.g., simple save without specific actions)
                    // This case should ideally not be reached if all actions are handled above.
                    return callback(null, object);
            }

            if (membershipIndex > -1) {
                organization.organizationMemberships[membershipIndex] = {
                    ...organization.organizationMemberships[membershipIndex],
                    ...dataToSave
                };
            } else {
                organization.organizationMemberships = organization.organizationMemberships || [];
                organization.organizationMemberships.push({
                    _id: new global.ObjectID(),
                    exercise: { id: object.exercise.id, code: object.exercise.code },
                    ...dataToSave
                });

            }

            organization.member = {...object.member}

            await global.app.C.Organization.save(organization,
                {
                    ...basicContext(context),
                    fieldPath: [
                        'member.id',
                        "files",
                        'organizationMemberships.exercise.id', 'organizationMemberships.exercise.code',
                        'organizationMemberships.status.id', 'organizationMemberships.amount', 'organizationMemberships.feeAmount', 'organizationMemberships.donationAmount',
                        'organizationMemberships.paymentDate', 'organizationMemberships.paymentMethod.id',
                        'organizationMemberships.lastModificationDate', 'organizationMemberships.lastModificationUser.id',
                        'organizationMemberships.membershipCard.id', 'organizationMemberships.membershipCard.filename',
                        'organizationMemberships.accountStatement.id', 'organizationMemberships.accountStatement.filename',
                        'organizationMemberships.sendingCounter'
                    ]
                }
            );

            const resultObject = {
                ...object,
                ...dataToSave,
                organizationReferentsNumber: object.organizationReferents?.length || 0
            };

            callback(null, resultObject);
        } catch (e) {
            callback(e);
        }
    });
}

// --- Entity & Module Definitions ---

export const entities = [
    {
        name: 'CotisationStatus',
        type: 'static',
        fields: [{ path: 'id', type: 'string' }, 'name'],
        objects: [
            {id: 'settled', name: 'Réglé'},
            {id: 'unsettled', name: 'Non réglé'},
            {id: 'pending', name: 'À vérifier'}
        ]
    },
    {
        name: 'AdhesionContribution',
        facets: [
            {name: 'files', link: 'OTO', path: 'membershipCard'},
            {name: 'files', link: 'OTO', path: 'accountStatement'},
        ],
        fields: [
            {path: 'exercise', type: 'Exercise'},
            {path: 'organization', type: 'Organization'},
            {path: 'member', type: 'Member', nullable: true},
            {path: 'status', type: 'CotisationStatus'}, // Réglé, Non réglé, En attente
            {path: 'amount', type: 'decimal'}, // Montant reçu
            {path: 'feeAmount', type: 'decimal'}, // Montant cotisation
            {path: 'donationAmount', type: 'decimal'}, // Montant don
            {path: 'previousExerciseFee', type: 'decimal'}, // Cotisation Exercice précédent
            {path: 'paymentDate', type: 'date'},
            {path: 'paymentMethod', type: 'PaymentMethod'},
            {path: 'sendingCounter', type: 'integer'},
            {path: 'organizationReferents', type: 'object'},
            {path: 'lastModificationDate', type: 'date'},
            {path: 'lastModificationUser', type: 'User'}
        ],
        find: findContributions,
        get: getContribution,
        save: saveContribution
    }
];

export const module_ = {
    object: 'AdhesionContribution',
    name: 'MembershipFee',
    tKey: 'Cotisation',
    category: {
        path: 'menuTitle_adhesion',
        icon: 'euro'
    },
    removable: false,
    newable: false,
    defaultSortBy: 'organization',
    defaultSortDirection: 'ASC',
    /*
    actions: [
        {method: 'PUT', name: 'validateAndSend', tKey: 'Valider et envoyer', accessIdPath: 'accessId'},
        {method: 'PUT', name: 'saveAndInform', tKey: 'Enregistrer et informer référent(s)', accessIdPath: 'accessId'},
        {method: 'PUT', name: 'cancelPayment', tKey: 'Annuler affectation', accessIdPath: 'accessId'},
    ],
     */
    viewMap: {
        dt: [
            {path: 'exercise', display: 'code'},
            {path: 'organization'},
            {path: 'member', tKey: 'organizationSize'},
            {path: 'status'},
            {path: 'amount', tKey: 'Règlement (€)', tooltip: true},
            {path: 'feeAmount', tKey: 'Cotisation (€)', tooltip: true},
            {path: 'donationAmount', tKey: 'Don (€)', tooltip: true},
            {path: 'paymentDate'},
            {path: 'paymentMethod'},
            {path: 'sendingCounter', tKey: 'Nb. envoi'},
            {path: 'organizationReferentsNumber', tKey: 'Nb. référent(s)'}, // Display count or full names
            {path: 'lastModificationDate', tKey: 'Date & heure'},
            {path: 'lastModificationUser', tKey: 'Utilisateur'},
            {path: 'membershipCard', type: 'fileLink', tKey: 'Carte adhésion'},
            {path: 'accountStatement', type: 'fileLink', tKey: 'Justificatif'}
        ],
        form: {
            fields: [
                {path: 'status', disabled: true},
                {path: 'organization', fieldPath: ['id', 'code', 'name'], disabled: true},
                {path: 'exercise', display: 'code', fieldPath: ['id', 'code', 'exerciseStatus.id'], disabled: true},
                {path: 'member', tKey: 'organizationSize',
                    subscriptions: {
                        onChange: (newValue, oldValue, { module, store }) => {
                            const state = store.getState();
                            const amountField = module.viewMap.form.fields.find(field => field.path === 'amount');
                            const receivedAmount = amountField.getValue();
                            const expectedAmount = getExpectedAmount(newValue?.id);

                            const { feeAmount, donationAmount } = calculateAmounts(receivedAmount, expectedAmount);


                            const feeAmountField = module.viewMap.form.fields.find(field => field.path === 'feeAmount');
                            const donationAmountField = module.viewMap.form.fields.find(field => field.path === 'donationAmount');

                            feeAmountField.setValue(feeAmount)
                            donationAmountField.setValue(donationAmount)

                            const formValues = getFormValues(state)
                            updateFormButtons(store, formValues);
                        }
                    }
                },
                {path: 'paymentDate', required: true, endDate: moment().format('YYYY-MM-DD'),
                    subscriptions: {
                        onChange: (newValue, oldValue, { module, store }) => {
                            const isPaymentDateSet = !!newValue;

                            // Toggle visibility of related fields
                            store.dispatch(setFieldVisibility('e_amount', isPaymentDateSet));
                            store.dispatch(setFieldVisibility('e_previousExerciseFee', isPaymentDateSet));
                            store.dispatch(setFieldVisibility('e_feeAmount', isPaymentDateSet));
                            store.dispatch(setFieldVisibility('e_donationAmount', isPaymentDateSet));
                            store.dispatch(setFieldVisibility('e_paymentMethod', isPaymentDateSet));

                            // If paymentDate is cleared, also clear related amounts and method
                            if (!isPaymentDateSet) {
                                const amountField = module.viewMap.form.fields.find(field => field.path === 'amount');
                                const feeAmountField = module.viewMap.form.fields.find(field => field.path === 'feeAmount');
                                const donationAmountField = module.viewMap.form.fields.find(field => field.path === 'donationAmount');
                                const paymentMethodField = module.viewMap.form.fields.find(field => field.path === 'paymentMethod');

                                amountField.setValue(null)
                                feeAmountField.setValue(null)
                                donationAmountField.setValue(null)
                                paymentMethodField.setValue(null)
                            }
                            const state = store.getState();
                            const formValues = getFormValues(state)

                            updateFormButtons(store, formValues);
                        }
                    }
                },
                {path: 'amount', tKey: 'Règlement (€)', required: true, minValue: 0,
                    subscriptions: {
                        onChange: (newValue, oldValue, { module, store }) => {
                            const state = store.getState();
                            const orgSizeField = module.viewMap.form.fields.find(field => field.path === 'member');
                            const orgSize = orgSizeField.getValue()
                            const expectedAmount = getExpectedAmount(orgSize?.id);

                            const { feeAmount, donationAmount } = calculateAmounts(newValue, expectedAmount);

                            const feeAmountField = module.viewMap.form.fields.find(field => field.path === 'feeAmount');
                            const donationAmountField = module.viewMap.form.fields.find(field => field.path === 'donationAmount');

                            feeAmountField.setValue(feeAmount)
                            donationAmountField.setValue(donationAmount)

                            const formValues = getFormValues(state)
                            updateFormButtons(store, formValues);
                        }
                    }
                },
                {path: 'previousExerciseFee', tKey: 'Cotisation Exercice précédent', disabled: true},
                {path: 'feeAmount', tKey: 'Cotisation (€)', disabled: true},
                {path: 'donationAmount', tKey: 'Don (€)', disabled: true},
                {path: 'paymentMethod', required: true, sortList: false},
                {
                    path: 'organizationReferents', tKey: 'Référent(s) organisation', type: 'dtObjects',
                    disabled: true,
                    fields: [
                        {path: 'firstname'},
                        {path: 'lastname'},
                        {path: 'mail', width: 300},
                        {path: 'phone'},
                        {path: 'functions', width: 300},
                        {path: 'active', type: 'boolean', width: 100}
                    ]
                }
            ]
        }
    },
    filters: [
        {
            path: 'exercise',
            object: 'Exercise',
            display: 'code',
            tKey: 'Exercice',
            client: true,
            clearable: true,
            sortList: false,
            objectFieldPath: ['id', 'code', 'exerciseStatus.id'],
        },
        {
            path: 'association',
            object: 'Organization',
            display: "name",
            tKey: 'Association',
            type: 'tags',
            filters: ['isMemberAssociation'],
            default: [],
            client: true,
        },
        {
            path: 'cotisation',
            tKey: 'Statut',
            object: 'CotisationStatus',
            display: "name",
            type: 'tags',
            client: true,
            default: [{id: 'settled', name: 'Réglé'}, {id: 'unsettled', name: 'Non réglé'}, {id: 'pending', name: 'À vérifier'}]
        },
    ],
    actionSubscriptions: [
        {
            actionType: GET_OBJECT_SUCCESS,
            subscription: ({ store }) => {
                const state = store.getState();

                const formData = _.get(state, 'edit.object.data');

                const paymentDate = _.get(formData, 'paymentDate');
                const exerciseStatus = _.get(formData, 'exercise.exerciseStatus.id');
                const membershipStatus = _.get(formData, 'status.id');

                const isClosed = exerciseStatus === 'finalClosure';
                const isSettled = membershipStatus === 'settled';
                const isPaymentDateSet = !!paymentDate;

                // Field Edition (disabled if closed)
                store.dispatch(setFieldEdition('e_member', !isClosed && !isSettled));
                store.dispatch(setFieldEdition('e_paymentDate', !isClosed && !isSettled));
                store.dispatch(setFieldEdition('e_amount', !isClosed && !isSettled));
                store.dispatch(setFieldEdition('e_paymentMethod', !isClosed && !isSettled));

                // Field Visibility (hidden if paymentDate not set)
                store.dispatch(setFieldVisibility('e_amount', isPaymentDateSet));
                store.dispatch(setFieldVisibility('e_previousExerciseFee', isPaymentDateSet));
                store.dispatch(setFieldVisibility('e_feeAmount', isPaymentDateSet));
                store.dispatch(setFieldVisibility('e_donationAmount', isPaymentDateSet));
                store.dispatch(setFieldVisibility('e_paymentMethod', isPaymentDateSet));

                updateFormButtons(store, formData); // Initial call to update buttons
            }
        }
    ]
};
